import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import type {
  CanvasSettings,
  EditorState,
  EditorAction,
  EditorElement,
  AnswerKeyValue,
  GroupData,
} from "./types";
import {
  DEFAULT_EDITOR_CANVAS_HEIGHT,
  DEFAULT_EDITOR_CANVAS_WIDTH,
} from "../features/question-editor/lib/canvas";
import { flattenGroups } from "./grouping";
import {
  STANDARD_COLOR_PALETTE,
  normalizeStandardColorPalette,
  normalizeHexColor,
  areSameColor,
} from "../features/question-editor/lib/colorPalette";

const CANVAS_SETTINGS_STORAGE_KEY = "question-editor:canvas-settings";
const HISTORY_LIMIT = 15;

export const DEFAULT_CANVAS_SETTINGS: CanvasSettings = {
  width: DEFAULT_EDITOR_CANVAS_WIDTH,
  height: DEFAULT_EDITOR_CANVAS_HEIGHT,
  lockWidth: false,
  background: "white",
  colorPalette: [...STANDARD_COLOR_PALETTE],
  expandable: true,
  padding: {
    x: 32,
    y: 32,
  },
  innerPadding: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  grid: {
    enabled: true,
    size: 28,
  },
  hiddenArea: {
    enabled: false,
    size: 64,
  },
  question: {
    questionType: undefined,
    grade: "",
    lesson: "",
    subject: "",
    topic: "",
    learningOutcomes: "",
    difficulty: 50,
    trackTime: false,
    displayInMba: false,
    notMobileFriendly: false,
    timeLimitSeconds: 0,
    solutionVideoUrl: "",
    topicVideoUrl: "",
    solutionTextUrl: "",
  },
};

const cloneCanvasSettings = (settings: CanvasSettings): CanvasSettings => ({
  ...settings,
  colorPalette: normalizeStandardColorPalette(settings.colorPalette),
  padding: { ...settings.padding },
  innerPadding: settings.innerPadding
    ? { ...settings.innerPadding }
    : undefined,
  grid: { ...settings.grid },
  hiddenArea: { ...settings.hiddenArea },
  question: { ...settings.question },
});

// Bir palet slotunun rengi değiştiğinde, eski rengi kullanan tüm canvas/
// element string alanlarını yeni renkle değiştirir (derin gezinme).
function replaceColorStrings<T>(value: T, oldColor: string, newColor: string): T {
  if (typeof value === "string") {
    return (areSameColor(value, oldColor) ? newColor : value) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) =>
      replaceColorStrings(item, oldColor, newColor)
    ) as T;
  }

  if (value && typeof value === "object") {
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(
      value as Record<string, unknown>
    )) {
      const replaced = replaceColorStrings(entry, oldColor, newColor);
      next[key] = replaced;
      if (replaced !== entry) {
        changed = true;
      }
    }
    return (changed ? next : value) as T;
  }

  return value;
}

const deepClone = <T,>(value: T): T => {
  if (typeof structuredClone === "function") {
    try {
      return structuredClone(value);
    } catch {
      // Fallback to JSON clone below.
    }
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

const cloneCanvasForHistory = (settings: CanvasSettings): CanvasSettings => {
  const cloned = cloneCanvasSettings(settings);
  if (cloned.question && cloned.question.answerKey) {
    cloned.question = {
      ...cloned.question,
      answerKey: deepClone(cloned.question.answerKey),
    };
  }
  return cloned;
};

const cloneElementForHistory = (element: EditorElement): EditorElement => ({
  ...element,
  position: { ...element.position },
  size: { ...element.size },
  data: deepClone(element.data),
});

type EditorSnapshot = {
  canvas: CanvasSettings;
  elements: EditorElement[];
  assets: EditorState["assets"];
  hiddenElements: EditorState["hiddenElements"];
};

const createSnapshot = (state: EditorState): EditorSnapshot => ({
  canvas: cloneCanvasForHistory(state.canvas),
  elements: state.elements.map(cloneElementForHistory),
  assets: state.assets.map((asset) => ({ ...asset })),
  hiddenElements: new Set(state.hiddenElements),
});

const restoreSnapshot = (
  state: EditorState,
  snapshot: EditorSnapshot
): EditorState => ({
  ...state,
  canvas: cloneCanvasForHistory(snapshot.canvas),
  elements: snapshot.elements.map(cloneElementForHistory),
  assets: snapshot.assets.map((asset) => ({ ...asset })),
  hiddenElements: new Set(snapshot.hiddenElements),
  selectedElementId: null,
  selectedElementIds: [],
});

const initialElements: EditorElement[] = [];

const initialState: EditorState = {
  canvas: cloneCanvasSettings(DEFAULT_CANVAS_SETTINGS),
  elements: initialElements,
  selectedElementId: null,
  selectedElementIds: [],
  zoom: 1,
  assets: [],
  hiddenElements: new Set(),
};

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  const canvasPaddingLeft = state.canvas.padding?.x || 0;
  const canvasPaddingTop = state.canvas.padding?.y || 0;
  const canvasInnerPaddingLeft = state.canvas.innerPadding?.left || 0;
  const canvasInnerPaddingTop = state.canvas.innerPadding?.top || 0;
  const canvasInnerPaddingRight = state.canvas.innerPadding?.right || 0;
  const canvasInnerPaddingBottom = state.canvas.innerPadding?.bottom || 0;
  const canvasContentLeft = canvasPaddingLeft + canvasInnerPaddingLeft;
  const canvasContentTop = canvasPaddingTop + canvasInnerPaddingTop;
  const canvasContentRight = state.canvas.width - canvasPaddingLeft - canvasInnerPaddingRight;
  const canvasContentBottom = state.canvas.height - canvasPaddingTop - canvasInnerPaddingBottom;
  const canvasContentCenterX = (canvasContentLeft + canvasContentRight) / 2;
  const canvasContentCenterY = (canvasContentTop + canvasContentBottom) / 2;

  switch (action.type) {
    case "select-element":
      // Legacy support: maintain selectedElementId for backward compatibility
      return {
        ...state,
        selectedElementId: action.elementId,
        selectedElementIds: action.elementId ? [action.elementId] : [],
      };
    case "select-elements":
      return {
        ...state,
        selectedElementIds: action.elementIds,
        selectedElementId: action.elementIds.length === 1 ? action.elementIds[0] : action.elementIds.length > 0 ? action.elementIds[0] : null,
      };
    case "toggle-element-selection": {
      const currentIds = state.selectedElementIds;
      const isSelected = currentIds.includes(action.elementId);
      const newIds = isSelected
        ? currentIds.filter((id) => id !== action.elementId)
        : [...currentIds, action.elementId];
      return {
        ...state,
        selectedElementIds: newIds,
        selectedElementId: newIds.length === 1 ? newIds[0] : newIds.length > 0 ? newIds[0] : null,
      };
    }
    case "update-element":
      return {
        ...state,
        elements: state.elements.map((element) =>
          element.id === action.elementId
            ? {
                ...element,
                ...action.changes,
                data: { ...element.data, ...action.changes.data },
              }
            : element
        ),
      };
    case "move-element":
      return {
        ...state,
        elements: state.elements.map((element) =>
          element.id === action.elementId
            ? {
                ...element,
                position: { ...element.position, ...action.position },
              }
            : element
        ),
      };
    case "move-elements": {
      const elementIdsSet = new Set(action.elementIds);
      return {
        ...state,
        elements: state.elements.map((element) =>
          elementIdsSet.has(element.id)
            ? {
                ...element,
                position: (() => {
                  // Get initial position from the map
                  const initialPos = action.initialPositions.get(element.id);
                  if (initialPos) {
                    // Calculate new position from initial position + delta
                    return {
                      x: initialPos.x + action.delta.x,
                      y: initialPos.y + action.delta.y,
                    };
                  }
                  // Fallback: use current position + delta
                  return {
                    x: element.position.x + action.delta.x,
                    y: element.position.y + action.delta.y,
                  };
                })(),
              }
            : element
        ),
      };
    }
    case "add-element":
      return {
        ...state,
        elements: [...state.elements, action.element],
        selectedElementId: action.element.id,
        selectedElementIds: [action.element.id],
      };
    case "add-elements": {
      if (action.elements.length === 0) {
        return state;
      }
      const newIds = action.elements.map((element) => element.id);
      return {
        ...state,
        elements: [...state.elements, ...action.elements],
        selectedElementId: newIds[newIds.length - 1],
        selectedElementIds: newIds,
      };
    }
    case "delete-elements": {
      if (action.elementIds.length === 0) {
        return state;
      }
      const removeSet = new Set(action.elementIds);
      const updatedElements = state.elements.filter(
        (element) => !removeSet.has(element.id)
      );

      // Clean up answer keys for any deleted elements
      let nextCanvas = state.canvas;
      const answerKey = state.canvas.question?.answerKey;
      if (answerKey && action.elementIds.some((id) => answerKey[id])) {
        const remainingAnswerKey = { ...answerKey };
        action.elementIds.forEach((id) => {
          delete remainingAnswerKey[id];
        });
        nextCanvas = {
          ...state.canvas,
          question: {
            ...state.canvas.question,
            answerKey: remainingAnswerKey,
          },
        };
      }

      const updatedSelectedIds = state.selectedElementIds.filter(
        (id) => !removeSet.has(id)
      );
      return {
        ...state,
        elements: updatedElements,
        selectedElementId:
          state.selectedElementId && removeSet.has(state.selectedElementId)
            ? updatedSelectedIds.length > 0
              ? updatedSelectedIds[0]
              : null
            : state.selectedElementId,
        selectedElementIds: updatedSelectedIds,
        canvas: nextCanvas,
      };
    }
    case "delete-element": {
      const updatedElements = state.elements.filter(
        (element) => element.id !== action.elementId
      );

      // Clean up answer key if element is deleted
      const answerKey = state.canvas.question?.answerKey;
      if (answerKey && answerKey[action.elementId]) {
        const { [action.elementId]: _, ...remainingAnswerKey } = answerKey;
        const updatedSelectedIds = state.selectedElementIds.filter(
          (id) => id !== action.elementId
        );
        return {
          ...state,
          elements: updatedElements,
          selectedElementId:
            state.selectedElementId === action.elementId
              ? updatedSelectedIds.length > 0
                ? updatedSelectedIds[0]
                : null
              : state.selectedElementId,
          selectedElementIds: updatedSelectedIds,
          canvas: {
            ...state.canvas,
            question: {
              ...state.canvas.question,
              answerKey: remainingAnswerKey,
            },
          },
        };
      }

      const updatedSelectedIds = state.selectedElementIds.filter(
        (id) => id !== action.elementId
      );
      return {
        ...state,
        elements: updatedElements,
        selectedElementId:
          state.selectedElementId === action.elementId
            ? updatedSelectedIds.length > 0
              ? updatedSelectedIds[0]
              : null
            : state.selectedElementId,
        selectedElementIds: updatedSelectedIds,
      };
    }
    case "set-zoom":
      return { ...state, zoom: action.zoom };
    case "update-color-palette-slot": {
      const nextColor = normalizeHexColor(action.color);
      const currentPalette = normalizeStandardColorPalette(
        state.canvas.colorPalette
      );

      if (
        !nextColor ||
        action.index < 0 ||
        action.index >= currentPalette.length
      ) {
        return state;
      }

      const oldColor = currentPalette[action.index];
      if (areSameColor(oldColor, nextColor)) {
        return state;
      }

      const nextPalette = currentPalette.map((color, index) =>
        index === action.index ? nextColor : color
      );
      // Slot rengini değiştirmek, o rengi kullanan tüm canvas/element
      // alanlarını da günceller (color-palette elemanı hariç).
      const replacedCanvas = replaceColorStrings(
        state.canvas,
        oldColor,
        nextColor
      );

      return {
        ...state,
        canvas: {
          ...replacedCanvas,
          colorPalette: nextPalette,
        },
        elements: state.elements.map((element) => {
          if (element.type === "color-palette") {
            return element;
          }

          const nextData = replaceColorStrings(
            element.data,
            oldColor,
            nextColor
          );
          return nextData === element.data
            ? element
            : { ...element, data: nextData };
        }),
      };
    }
    case "update-canvas":
      return {
        ...state,
        canvas: {
          ...state.canvas,
          ...action.changes,
          colorPalette: action.changes.colorPalette
            ? normalizeStandardColorPalette(action.changes.colorPalette)
            : state.canvas.colorPalette,
          // Deep merge nested objects
          padding: action.changes.padding
            ? { ...state.canvas.padding, ...action.changes.padding }
            : state.canvas.padding,
          innerPadding:
            action.changes.innerPadding !== undefined
              ? action.changes.innerPadding
                ? {
                    ...state.canvas.innerPadding,
                    ...action.changes.innerPadding,
                  }
                : action.changes.innerPadding
              : state.canvas.innerPadding,
          grid: action.changes.grid
            ? { ...state.canvas.grid, ...action.changes.grid }
            : state.canvas.grid,
          hiddenArea: action.changes.hiddenArea
            ? { ...state.canvas.hiddenArea, ...action.changes.hiddenArea }
            : state.canvas.hiddenArea,
          question: action.changes.question
            ? {
                ...state.canvas.question,
                ...action.changes.question,
                // Preserve existing answerKey unless the caller explicitly provided one.
                // Important: we must allow callers to CLEAR the answerKey by passing
                // `answerKey: undefined`. Checking `!== undefined` would treat that as
                // "not provided" and would cause infinite update loops in sanitizer effects.
                answerKey:
                  "answerKey" in action.changes.question
                    ? action.changes.question.answerKey
                    : state.canvas.question?.answerKey,
              }
            : state.canvas.question,
        },
      };
    case "reorder-element": {
      const sorted = [...state.elements].sort((a, b) => a.zIndex - b.zIndex);
      const index = sorted.findIndex(
        (element) => element.id === action.elementId
      );
      if (index === -1) {
        return state;
      }

      let targetIndex = index;
      switch (action.direction) {
        case "forward":
          targetIndex = Math.min(sorted.length - 1, index + 1);
          break;
        case "backward":
          targetIndex = Math.max(0, index - 1);
          break;
        case "front":
          targetIndex = sorted.length - 1;
          break;
        case "back":
          targetIndex = 0;
          break;
        default:
          targetIndex = index;
      }

      if (targetIndex === index) {
        return state;
      }

      const [moved] = sorted.splice(index, 1);
      sorted.splice(targetIndex, 0, moved);

      const zIndexMap = new Map<string, number>();
      sorted.forEach((element, idx) => {
        zIndexMap.set(element.id, idx + 1);
      });

      return {
        ...state,
        elements: state.elements.map((element) =>
          zIndexMap.has(element.id)
            ? { ...element, zIndex: zIndexMap.get(element.id)! }
            : element
        ),
      };
    }
    case "set-state": {
      const nextCanvas = cloneCanvasSettings(action.state.canvas);
      return {
        ...state,
        canvas: nextCanvas,
        elements: action.state.elements.map((element) => ({ ...element })),
        assets: action.state.assets ? [...action.state.assets] : [],
        selectedElementId: null,
        selectedElementIds: [],
      };
    }
    case "align-elements": {
      const elementIdsSet = new Set(action.elementIds);
      const selectedElements = state.elements.filter((el) => elementIdsSet.has(el.id));
      
      if (selectedElements.length < 2) {
        return state;
      }

      let alignedElements: EditorElement[];
      const SPACING = 10; // Minimum spacing to prevent overlap
      
      switch (action.alignment) {
        case "left": {
          const minX = Math.min(...selectedElements.map((el) => el.position.x));
          // Sort by original Y position to maintain order
          const sortedByY = [...selectedElements].sort((a, b) => a.position.y - b.position.y);
          alignedElements = [];
          for (let i = 0; i < sortedByY.length; i++) {
            const el = sortedByY[i];
            let newY = el.position.y;
            // Check for overlaps with previous elements
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevBottom = prevEl.position.y + (prevEl.size.height || prevEl.size.width || 0);
              const currentTop = el.position.y;
              if (currentTop < prevBottom + SPACING) {
                newY = prevBottom + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: { ...el.position, x: minX, y: newY },
            });
          }
          break;
        }
        case "right": {
          const maxRight = Math.max(
            ...selectedElements.map((el) => el.position.x + (el.size.width || 0))
          );
          const sortedByY = [...selectedElements].sort((a, b) => a.position.y - b.position.y);
          alignedElements = [];
          for (let i = 0; i < sortedByY.length; i++) {
            const el = sortedByY[i];
            let newY = el.position.y;
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevBottom = prevEl.position.y + (prevEl.size.height || prevEl.size.width || 0);
              const currentTop = el.position.y;
              if (currentTop < prevBottom + SPACING) {
                newY = prevBottom + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: {
                ...el.position,
                x: maxRight - (el.size.width || 0),
                y: newY,
              },
            });
          }
          break;
        }
        case "center": {
          const minX = Math.min(...selectedElements.map((el) => el.position.x));
          const maxRight = Math.max(
            ...selectedElements.map((el) => el.position.x + (el.size.width || 0))
          );
          const centerX = (minX + maxRight) / 2;
          const sortedByY = [...selectedElements].sort((a, b) => a.position.y - b.position.y);
          alignedElements = [];
          for (let i = 0; i < sortedByY.length; i++) {
            const el = sortedByY[i];
            let newY = el.position.y;
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevBottom = prevEl.position.y + (prevEl.size.height || prevEl.size.width || 0);
              const currentTop = el.position.y;
              if (currentTop < prevBottom + SPACING) {
                newY = prevBottom + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: {
                ...el.position,
                x: centerX - (el.size.width || 0) / 2,
                y: newY,
              },
            });
          }
          break;
        }
        case "top": {
          const minY = Math.min(...selectedElements.map((el) => el.position.y));
          // Sort by original X position to maintain order
          const sortedByX = [...selectedElements].sort((a, b) => a.position.x - b.position.x);
          alignedElements = [];
          for (let i = 0; i < sortedByX.length; i++) {
            const el = sortedByX[i];
            let newX = el.position.x;
            // Check for overlaps with previous elements
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevRight = prevEl.position.x + (prevEl.size.width || 0);
              const currentLeft = el.position.x;
              if (currentLeft < prevRight + SPACING) {
                newX = prevRight + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: { ...el.position, x: newX, y: minY },
            });
          }
          break;
        }
        case "bottom": {
          const maxBottom = Math.max(
            ...selectedElements.map((el) => el.position.y + (el.size.height || el.size.width || 0))
          );
          const sortedByX = [...selectedElements].sort((a, b) => a.position.x - b.position.x);
          alignedElements = [];
          for (let i = 0; i < sortedByX.length; i++) {
            const el = sortedByX[i];
            let newX = el.position.x;
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevRight = prevEl.position.x + (prevEl.size.width || 0);
              const currentLeft = el.position.x;
              if (currentLeft < prevRight + SPACING) {
                newX = prevRight + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: {
                ...el.position,
                x: newX,
                y: maxBottom - (el.size.height || el.size.width || 0),
              },
            });
          }
          break;
        }
        case "middle": {
          const minY = Math.min(...selectedElements.map((el) => el.position.y));
          const maxBottom = Math.max(
            ...selectedElements.map((el) => el.position.y + (el.size.height || el.size.width || 0))
          );
          const centerY = (minY + maxBottom) / 2;
          const sortedByX = [...selectedElements].sort((a, b) => a.position.x - b.position.x);
          alignedElements = [];
          for (let i = 0; i < sortedByX.length; i++) {
            const el = sortedByX[i];
            let newX = el.position.x;
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevRight = prevEl.position.x + (prevEl.size.width || 0);
              const currentLeft = el.position.x;
              if (currentLeft < prevRight + SPACING) {
                newX = prevRight + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: {
                ...el.position,
                x: newX,
                y: centerY - (el.size.height || el.size.width || 0) / 2,
              },
            });
          }
          break;
        }
        default:
          return state;
      }

      return {
        ...state,
        elements: state.elements.map((element) => {
          const aligned = alignedElements.find((el) => el.id === element.id);
          return aligned || element;
        }),
      };
    }
    case "align-elements-to-canvas": {
      const elementIdsSet = new Set(action.elementIds);
      const selectedElements = state.elements.filter((el) => elementIdsSet.has(el.id));
      
      if (selectedElements.length < 1) {
        return state;
      }

      let alignedElements: EditorElement[];
      const SPACING = 10; // Minimum spacing to prevent overlap
      
      switch (action.alignment) {
        case "canvas-left": {
          // Sort by original Y position to maintain order
          const sortedByY = [...selectedElements].sort((a, b) => a.position.y - b.position.y);
          alignedElements = [];
          for (let i = 0; i < sortedByY.length; i++) {
            const el = sortedByY[i];
            let newY = el.position.y;
            // Check for overlaps with previous elements
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevBottom = prevEl.position.y + (prevEl.size.height || prevEl.size.width || 0);
              const currentTop = el.position.y;
              if (currentTop < prevBottom + SPACING) {
                newY = prevBottom + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: { ...el.position, x: canvasContentLeft, y: newY },
            });
          }
          break;
        }
        case "canvas-right": {
          const sortedByY = [...selectedElements].sort((a, b) => a.position.y - b.position.y);
          alignedElements = [];
          for (let i = 0; i < sortedByY.length; i++) {
            const el = sortedByY[i];
            let newY = el.position.y;
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevBottom = prevEl.position.y + (prevEl.size.height || prevEl.size.width || 0);
              const currentTop = el.position.y;
              if (currentTop < prevBottom + SPACING) {
                newY = prevBottom + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: {
                ...el.position,
                x: canvasContentRight - (el.size.width || 0),
                y: newY,
              },
            });
          }
          break;
        }
        case "canvas-top": {
          // Sort by original X position to maintain order
          const sortedByX = [...selectedElements].sort((a, b) => a.position.x - b.position.x);
          alignedElements = [];
          for (let i = 0; i < sortedByX.length; i++) {
            const el = sortedByX[i];
            let newX = el.position.x;
            // Check for overlaps with previous elements
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevRight = prevEl.position.x + (prevEl.size.width || 0);
              const currentLeft = el.position.x;
              if (currentLeft < prevRight + SPACING) {
                newX = prevRight + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: { ...el.position, x: newX, y: canvasContentTop },
            });
          }
          break;
        }
        case "canvas-bottom": {
          const sortedByX = [...selectedElements].sort((a, b) => a.position.x - b.position.x);
          alignedElements = [];
          for (let i = 0; i < sortedByX.length; i++) {
            const el = sortedByX[i];
            let newX = el.position.x;
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevRight = prevEl.position.x + (prevEl.size.width || 0);
              const currentLeft = el.position.x;
              if (currentLeft < prevRight + SPACING) {
                newX = prevRight + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: {
                ...el.position,
                x: newX,
                y: canvasContentBottom - (el.size.height || el.size.width || 0),
              },
            });
          }
          break;
        }
        case "canvas-center-h": {
          const sortedByY = [...selectedElements].sort((a, b) => a.position.y - b.position.y);
          alignedElements = [];
          for (let i = 0; i < sortedByY.length; i++) {
            const el = sortedByY[i];
            let newY = el.position.y;
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevBottom = prevEl.position.y + (prevEl.size.height || prevEl.size.width || 0);
              const currentTop = el.position.y;
              if (currentTop < prevBottom + SPACING) {
                newY = prevBottom + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: {
                ...el.position,
                x: canvasContentCenterX - (el.size.width || 0) / 2,
                y: newY,
              },
            });
          }
          break;
        }
        case "canvas-center-v": {
          const sortedByX = [...selectedElements].sort((a, b) => a.position.x - b.position.x);
          alignedElements = [];
          for (let i = 0; i < sortedByX.length; i++) {
            const el = sortedByX[i];
            let newX = el.position.x;
            if (i > 0) {
              const prevEl = alignedElements[i - 1];
              const prevRight = prevEl.position.x + (prevEl.size.width || 0);
              const currentLeft = el.position.x;
              if (currentLeft < prevRight + SPACING) {
                newX = prevRight + SPACING;
              }
            }
            alignedElements.push({
              ...el,
              position: {
                ...el.position,
                x: newX,
                y: canvasContentCenterY - (el.size.height || el.size.width || 0) / 2,
              },
            });
          }
          break;
        }
        default:
          return state;
      }

      return {
        ...state,
        elements: state.elements.map((element) => {
          const aligned = alignedElements.find((el) => el.id === element.id);
          return aligned || element;
        }),
      };
    }
    case "distribute-elements": {
      const elementIdsSet = new Set(action.elementIds);
      const selectedElements = state.elements.filter((el) => elementIdsSet.has(el.id));
      
      if (selectedElements.length < 3) {
        return state;
      }

      // Sort elements by position
      const sortedElements = [...selectedElements].sort((a, b) => {
        if (action.direction === "horizontal") {
          return a.position.x - b.position.x;
        } else {
          return a.position.y - b.position.y;
        }
      });

      let distributedElements: EditorElement[];
      const SPACING = 10; // Minimum spacing to prevent overlap

      if (action.direction === "horizontal") {
        const firstX = sortedElements[0].position.x;
        const lastX =
          sortedElements[sortedElements.length - 1].position.x +
          (sortedElements[sortedElements.length - 1].size.width || 0);
        const totalWidth = lastX - firstX;
        const totalElementWidth = sortedElements.reduce(
          (sum, el) => sum + (el.size.width || 0),
          0
        );
        const gap = (totalWidth - totalElementWidth) / (sortedElements.length - 1);

        let currentX = firstX;
        distributedElements = [];
        // Sort by Y to maintain order for overlap prevention
        const sortedByY = [...sortedElements].sort((a, b) => a.position.y - b.position.y);
        const xPositions = new Map<string, number>();
        
        // First, calculate X positions
        for (const el of sortedElements) {
          xPositions.set(el.id, currentX);
          currentX += (el.size.width || 0) + gap;
        }
        
        // Then apply X positions and adjust Y to prevent overlap
        for (let i = 0; i < sortedByY.length; i++) {
          const el = sortedByY[i];
          let newY = el.position.y;
          if (i > 0) {
            const prevEl = distributedElements[i - 1];
            const prevBottom = prevEl.position.y + (prevEl.size.height || prevEl.size.width || 0);
            const currentTop = el.position.y;
            if (currentTop < prevBottom + SPACING) {
              newY = prevBottom + SPACING;
            }
          }
          distributedElements.push({
            ...el,
            position: { 
              ...el.position, 
              x: xPositions.get(el.id) || el.position.x,
              y: newY,
            },
          });
        }
      } else {
        const firstY = sortedElements[0].position.y;
        const lastY =
          sortedElements[sortedElements.length - 1].position.y +
          (sortedElements[sortedElements.length - 1].size.height || sortedElements[sortedElements.length - 1].size.width || 0);
        const totalHeight = lastY - firstY;
        const totalElementHeight = sortedElements.reduce(
          (sum, el) => sum + (el.size.height || el.size.width || 0),
          0
        );
        const gap = (totalHeight - totalElementHeight) / (sortedElements.length - 1);

        let currentY = firstY;
        distributedElements = [];
        // Sort by X to maintain order for overlap prevention
        const sortedByX = [...sortedElements].sort((a, b) => a.position.x - b.position.x);
        const yPositions = new Map<string, number>();
        
        // First, calculate Y positions
        for (const el of sortedElements) {
          yPositions.set(el.id, currentY);
          currentY += (el.size.height || el.size.width || 0) + gap;
        }
        
        // Then apply Y positions and adjust X to prevent overlap
        for (let i = 0; i < sortedByX.length; i++) {
          const el = sortedByX[i];
          let newX = el.position.x;
          if (i > 0) {
            const prevEl = distributedElements[i - 1];
            const prevRight = prevEl.position.x + (prevEl.size.width || 0);
            const currentLeft = el.position.x;
            if (currentLeft < prevRight + SPACING) {
              newX = prevRight + SPACING;
            }
          }
          distributedElements.push({
            ...el,
            position: { 
              ...el.position, 
              x: newX,
              y: yPositions.get(el.id) || el.position.y,
            },
          });
        }
      }

      return {
        ...state,
        elements: state.elements.map((element) => {
          const distributed = distributedElements.find((el) => el.id === element.id);
          return distributed || element;
        }),
      };
    }
    case "show-elements": {
      const newHiddenElements = new Set(state.hiddenElements);
      action.elementIds.forEach((id) => newHiddenElements.delete(id));
      return {
        ...state,
        hiddenElements: newHiddenElements,
      };
    }
    case "hide-elements": {
      const newHiddenElements = new Set(state.hiddenElements);
      action.elementIds.forEach((id) => newHiddenElements.add(id));
      return {
        ...state,
        hiddenElements: newHiddenElements,
      };
    }
    case "initialize-hidden-elements": {
      // Find all video elements with showElementsAfterVideo configuration
      const videoElements = state.elements.filter((el) => el.type === "slide");
      const elementsToHide = new Set<string>();

      videoElements.forEach((videoEl) => {
        const videoData = videoEl.data as { style?: { showElementsAfterVideo?: string[] } };
        const showElementsAfterVideo = videoData.style?.showElementsAfterVideo;
        if (showElementsAfterVideo && Array.isArray(showElementsAfterVideo)) {
          showElementsAfterVideo.forEach((id) => {
            // Only hide if element exists
            if (state.elements.some((el) => el.id === id)) {
              elementsToHide.add(id);
            }
          });
        }
      });

      return {
        ...state,
        hiddenElements: elementsToHide,
      };
    }
    case "group-elements": {
      const elementIdsSet = new Set(action.elementIds);
      const selectedElements = state.elements.filter((el) => elementIdsSet.has(el.id));

      if (selectedElements.length < 2) {
        return state;
      }

      // Check if any selected element is already a group (no nested grouping)
      if (selectedElements.some((el) => el.type === "group")) {
        return state;
      }

      // Calculate bounding box for the group
      const minX = Math.min(...selectedElements.map((el) => el.position.x));
      const minY = Math.min(...selectedElements.map((el) => el.position.y));
      const maxX = Math.max(...selectedElements.map((el) => el.position.x + (el.size.width || 0)));
      const maxY = Math.max(...selectedElements.map((el) => el.position.y + (el.size.height || el.size.width || 0)));

      // Create children with relative positions
      const children: EditorElement[] = selectedElements.map((el) => ({
        ...el,
        position: {
          x: el.position.x - minX,
          y: el.position.y - minY,
        },
      }));

      // Calculate new zIndex (highest of all grouped elements)
      const maxZIndex = Math.max(...selectedElements.map((el) => el.zIndex));

      // Create the group element
      const groupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const groupElement: EditorElement = {
        id: groupId,
        type: "group",
        position: { x: minX, y: minY },
        size: { width: maxX - minX, height: maxY - minY },
        zIndex: maxZIndex,
        data: {
          children,
          locked: false,
        } as unknown as Record<string, unknown>,
      };

      // Remove grouped elements from the list and add the group
      const newElements = state.elements
        .filter((el) => !elementIdsSet.has(el.id))
        .concat(groupElement);

      return {
        ...state,
        elements: newElements,
        selectedElementId: groupId,
        selectedElementIds: [groupId],
      };
    }
    case "ungroup-element": {
      const groupElement = state.elements.find((el) => el.id === action.groupId);

      if (!groupElement || groupElement.type !== "group") {
        return state;
      }

      const groupData = groupElement.data as unknown as GroupData;
      const children = groupData.children || [];

      if (children.length === 0) {
        return state;
      }

      // Convert children back to absolute positions
      const restoredElements: EditorElement[] = children.map((child) => ({
        ...child,
        position: {
          x: groupElement.position.x + child.position.x,
          y: groupElement.position.y + child.position.y,
        },
      }));

      // Remove group element and add restored children
      const newElements = state.elements
        .filter((el) => el.id !== action.groupId)
        .concat(restoredElements);

      // Select all ungrouped elements
      const restoredIds = restoredElements.map((el) => el.id);

      return {
        ...state,
        elements: newElements,
        selectedElementId: restoredIds.length > 0 ? restoredIds[0] : null,
        selectedElementIds: restoredIds,
      };
    }
    case "ungroup-all": {
      if (!state.elements.some((el) => el.type === "group")) {
        return state;
      }
      return {
        ...state,
        elements: flattenGroups(state.elements),
        selectedElementId: null,
        selectedElementIds: [],
      };
    }
    default:
      return state;
  }
}

type HistoryState = {
  past: EditorSnapshot[];
  present: EditorState;
  future: EditorSnapshot[];
  lastActionGroup: "move" | "resize" | "vertex" | null;
  gesture: "move" | "resize" | null;
};

const isAnswerKeyOnlyUpdate = (
  state: EditorState,
  changes: Partial<CanvasSettings>
): boolean => {
  const keys = Object.keys(changes);
  if (keys.length !== 1 || keys[0] !== "question") {
    return false;
  }
  const question = changes.question;
  if (!question) {
    return false;
  }
  const prevQuestion = state.canvas.question;
  const nextQuestion = {
    ...(prevQuestion ?? {}),
    ...question,
  };
  return Object.keys(nextQuestion).every((key) => {
    if (key === "answerKey") {
      return true;
    }
    return (prevQuestion as any)?.[key] === (nextQuestion as any)?.[key];
  });
};

const getActionGroup = (
  action: EditorAction
): "move" | "resize" | "vertex" | null => {
  if (action.type === "move-element" || action.type === "move-elements") {
    return "move";
  }
  if (
    action.type === "update-element" &&
    Object.keys(action.changes).length === 1
  ) {
    if ("size" in action.changes) {
      return "resize";
    }
    // Free-polygon vertex drags emit `{ data: { points } }` on every pointer
    // move; coalesce them so one drag = one undo step (and redo is preserved),
    // instead of flooding history with a snapshot per sub-pixel move.
    if ("data" in action.changes) {
      const data = (action.changes as { data?: Record<string, unknown> }).data;
      if (data && Object.keys(data).length === 1 && "points" in data) {
        return "vertex";
      }
    }
  }
  return null;
};

const shouldRecordHistory = (
  state: EditorState,
  action: EditorAction
): boolean => {
  switch (action.type) {
    case "select-element":
    case "select-elements":
    case "toggle-element-selection":
    case "set-zoom":
    case "show-elements":
    case "hide-elements":
    case "initialize-hidden-elements":
      return false;
    case "update-canvas":
      if (action.origin === "system") {
        return false;
      }
      return !isAnswerKeyOnlyUpdate(state, action.changes);
    case "set-state":
      return false;
    default:
      return true;
  }
};

const historyReducer = (state: HistoryState, action: EditorAction): HistoryState => {
  if (action.type === "begin-gesture") {
    if (state.gesture === action.gesture) {
      return state;
    }
    return {
      past: [...state.past, createSnapshot(state.present)].slice(-HISTORY_LIMIT),
      present: state.present,
      future: [],
      lastActionGroup: null,
      gesture: action.gesture,
    };
  }

  if (action.type === "end-gesture") {
    if (state.gesture !== action.gesture) {
      return state;
    }
    return {
      ...state,
      gesture: null,
      lastActionGroup: null,
    };
  }

  if (action.type === "undo") {
    if (state.past.length === 0) {
      return state;
    }
    const previous = state.past[state.past.length - 1];
    const nextPast = state.past.slice(0, -1);
    const nextFuture = [createSnapshot(state.present), ...state.future];
    return {
      past: nextPast,
      present: restoreSnapshot(state.present, previous),
      future: nextFuture,
      lastActionGroup: null,
      gesture: null,
    };
  }

  if (action.type === "redo") {
    if (state.future.length === 0) {
      return state;
    }
    const next = state.future[0];
    const nextFuture = state.future.slice(1);
    const nextPast = [...state.past, createSnapshot(state.present)];
    return {
      past: nextPast.slice(-HISTORY_LIMIT),
      present: restoreSnapshot(state.present, next),
      future: nextFuture,
      lastActionGroup: null,
      gesture: null,
    };
  }

  if (action.type === "set-state") {
    const nextPresent = editorReducer(state.present, action);
    return {
      past: [],
      present: nextPresent,
      future: [],
      lastActionGroup: null,
      gesture: null,
    };
  }

  const nextPresent = editorReducer(state.present, action);
  if (nextPresent === state.present) {
    return state;
  }

  if (state.gesture) {
    return {
      ...state,
      present: nextPresent,
    };
  }

  const actionGroup = getActionGroup(action);
  const recordHistory = shouldRecordHistory(state.present, action);
  const shouldPush =
    recordHistory && !(actionGroup && state.lastActionGroup === actionGroup);

  const nextPast = shouldPush
    ? [...state.past, createSnapshot(state.present)].slice(-HISTORY_LIMIT)
    : state.past;

  return {
    past: nextPast,
    present: nextPresent,
    future: recordHistory ? [] : state.future,
    lastActionGroup: actionGroup,
    gesture: state.gesture,
  };
};

interface EditorContextValue extends EditorState {
  dispatch: React.Dispatch<EditorAction>;
}

const EditorStateContext = createContext<EditorContextValue | undefined>(
  undefined
);

export const EditorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [history, dispatch] = useReducer(
    historyReducer,
    initialState,
    (defaultState) => {
      let hydratedState = defaultState;

      if (typeof window !== "undefined") {
        try {
          const raw = window.localStorage.getItem(CANVAS_SETTINGS_STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as Partial<CanvasSettings>;
            const { question: _persistedQuestion, ...parsedWithoutQuestion } =
              parsed;

            const merged: CanvasSettings = cloneCanvasSettings({
              ...defaultState.canvas,
              ...parsedWithoutQuestion,
              width: Math.max(
                defaultState.canvas.width,
                typeof parsedWithoutQuestion.width === "number"
                  ? parsedWithoutQuestion.width
                  : defaultState.canvas.width
              ),
              height: Math.max(
                defaultState.canvas.height,
                typeof parsedWithoutQuestion.height === "number"
                  ? parsedWithoutQuestion.height
                  : defaultState.canvas.height
              ),
              // Default boyutları koru, aspect ratio için lockWidth kapalı
              expandable: true,
              padding: {
                ...defaultState.canvas.padding,
                ...(parsedWithoutQuestion.padding ?? {}),
              },
              innerPadding: parsedWithoutQuestion.innerPadding
                ? {
                    ...defaultState.canvas.innerPadding,
                    ...parsedWithoutQuestion.innerPadding,
                  }
                : defaultState.canvas.innerPadding,
              grid: {
                ...defaultState.canvas.grid,
                ...(parsedWithoutQuestion.grid ?? {}),
              },
              hiddenArea: {
                ...defaultState.canvas.hiddenArea,
                ...(parsedWithoutQuestion.hiddenArea ?? {}),
              },
            });

            hydratedState = { ...defaultState, canvas: merged };
          }
        } catch (error) {
          hydratedState = defaultState;
        }
      }

      return {
        past: [],
        present: hydratedState,
        future: [],
        lastActionGroup: null,
        gesture: null,
      };
    }
  );
  const state = history.present;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const {
      width,
      height,
      lockWidth,
      background,
      colorPalette,
      expandable,
      padding,
      grid,
      hiddenArea,
    } = state.canvas;
    const payload: Partial<CanvasSettings> = {
      width,
      height,
      lockWidth,
      background,
      colorPalette: normalizeStandardColorPalette(colorPalette),
      expandable,
      padding,
      grid,
      hiddenArea,
    };

    window.localStorage.setItem(
      CANVAS_SETTINGS_STORAGE_KEY,
      JSON.stringify(payload)
    );
  }, [state.canvas]);

  const answerKey = state.canvas.question?.answerKey;

  useEffect(() => {
    if (!answerKey || Object.keys(answerKey).length === 0) {
      return;
    }

    const isMatchingAnswerConfig = (value: AnswerKeyValue): boolean => {
      if (Array.isArray(value)) return false;
      if (!value || typeof value !== 'object') return false;
      if ('acceptedItems' in value) return false;
      return 'connections' in value;
    };

    const hasMatchingAnswerKey = Object.entries(answerKey).some(
      ([key, value]) => {
        return key === "matching-connections" || isMatchingAnswerConfig(value);
      }
    );

    if (hasMatchingAnswerKey) {
      return;
    }

    const validElementIds = new Set(
      state.elements
        .filter(
          (element) =>
            element.type === "choice" ||
            element.type === "drop-target" ||
            element.type === "order-list" ||
            element.type === "paint-area" ||
            element.type === "push-to-talk" ||
            element.type === "cube-bar"
        )
        .map((element) => element.id)
    );
    const entries = Object.entries(answerKey);
    // Keep matching-connections and other matching answer keys
    const filteredEntries = entries.filter(([elementId, value]) => {
      // Keep matching answer keys (matching-connections or objects with connections property)
      if (
        elementId === "matching-connections" ||
        (!Array.isArray(value) &&
          !("acceptedItems" in value) &&
          "connections" in value)
      ) {
        return true;
      }
      // Keep fill-in-the-blanks answer keys
      if (
        !Array.isArray(value) &&
        !("acceptedItems" in value) &&
        !("connections" in value) &&
        "blanks" in value
      ) {
        return true;
      }
      // Keep painting answer keys
      if (
        !Array.isArray(value) &&
        !("acceptedItems" in value) &&
        !("connections" in value) &&
        !("blanks" in value) &&
        "paintSwatchId" in value
      ) {
        return true;
      }
      // Keep valid element IDs
      return validElementIds.has(elementId);
    });

    if (filteredEntries.length === entries.length) {
      return;
    }

    const sanitizedAnswerKey = filteredEntries.reduce(
      (acc, [elementId, values]) => {
        acc[elementId] = values;
        return acc;
      },
      {} as Record<string, AnswerKeyValue>
    );

    dispatch({
      type: "update-canvas",
      changes: {
        question: {
          ...state.canvas.question,
          answerKey:
            filteredEntries.length > 0 ? sanitizedAnswerKey : undefined,
        },
      },
    });
  }, [answerKey, state.elements, dispatch, state.canvas.question]);

  const value = useMemo(() => ({ ...state, dispatch }), [state, dispatch]);

  return (
    <EditorStateContext.Provider value={value}>
      {children}
    </EditorStateContext.Provider>
  );
};

export function useEditorState(): EditorContextValue {
  const context = useContext(EditorStateContext);
  if (!context) {
    throw new Error("useEditorState must be used within an EditorProvider");
  }
  return context;
}
